import * as yup from "yup"
import { DateTime } from "luxon"
import { Form, Formik } from "formik"
import { makeStyles } from "tss-react/mui"
import { DialogContent } from "@mui/material"
import { useAppLocalization } from "../../../../../../modules/localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { StandardDialog } from "../../../../dialogs/StandardDialog"
import { RepeatOrderDialogForm } from "./RepeatOrderDialogForm"
import { prepareInitialValues } from "./helpers"
import { DeliveryTimesField } from "../../form/formTypes"

const useStyles = makeStyles()((theme) => ({
    dialogContent: {
        padding: "2.5rem",
        paddingTop: "0",
        color: theme.palette.text.primary,
    },
}))

export type RepeatOrderFormValues = Readonly<{
    repeatType: "day" | "week" | string
    repeatInterval: number
    startDate: DateTime | null
    endType: "on" | "after" | string
    endDate: DateTime | null
    repeatCount: number
    weekDays: string[]
}>

type RepeatOrderDialogProps = Readonly<{
    repeatOrderDialog: boolean
    initialValues?: Partial<RepeatOrderFormValues> | null
    onClose: () => void
    handleSubmitRecurrence: (values: Partial<RepeatOrderFormValues>) => void
    deliveries: DeliveryTimesField
}>

export function RepeatOrderDialog({
    repeatOrderDialog,
    initialValues,
    onClose,
    handleSubmitRecurrence,
    deliveries,
}: RepeatOrderDialogProps) {
    const { l10n } = useAppLocalization()

    const { classes } = useStyles()

    const validationSchema = yup.object({
        repeatType: yup.string().required(l10n.getString("required-field")),
        repeatInterval: yup
            .number()
            .min(1, l10n.getString("required-field", { min: 1 }))
            .required(l10n.getString("required-field")),
        startDate: yup.mixed().required(l10n.getString("required-field")),
        endType: yup.string().oneOf(["on", "after"]).required(l10n.getString("required-field")),
        endDate: yup
            .mixed<DateTime>()
            .when("endType", {
                is: "on",
                then: (schema) => schema.required(l10n.getString("required-field")),
                otherwise: (schema) => schema.notRequired(),
            })
            // Cross-field guard: the date picker's `minDate` only styles the field red,
            // it does not block submit — so the ordering must also be enforced here.
            .test(
                "end-date-not-before-start-date",
                l10n.getString("create-order-drawer-repeat-order-end-before-start-error"),
                function (value) {
                    const { startDate, endType } = this.parent
                    if (endType !== "on" || !DateTime.isDateTime(value) || !DateTime.isDateTime(startDate)) {
                        return true
                    }
                    return value.startOf("day") >= startDate.startOf("day")
                },
            ),
        repeatCount: yup
            .number()
            .min(1, l10n.getString("required-field", { min: 1 }))
            .when("endType", {
                is: "after",
                then: (schema) => schema.required(l10n.getString("required-field")),
                otherwise: (schema) => schema.notRequired(),
            }),
        weekDays: yup
            .array()
            .of(yup.string())
            .when("repeatType", {
                is: "week",
                then: (schema) =>
                    schema.min(1, l10n.getString("required-field")).required(l10n.getString("required-field")),
                otherwise: (schema) => schema.notRequired(),
            }),
    })

    function handleSubmit(values: RepeatOrderFormValues) {
        const filteredData: Partial<RepeatOrderFormValues> = {
            repeatType: values.repeatType,
            repeatInterval: values.repeatInterval,
            startDate: values.startDate,
            ...(values.repeatType === "week" ? { weekDays: values.weekDays } : {}),
            endType: values.endType,
            ...(values.endType === "on" ? { endDate: values.endDate } : {}),
            ...(values.endType === "after" ? { repeatCount: values.repeatCount } : {}),
        }
        handleSubmitRecurrence(filteredData)
    }

    return (
        <StandardDialog
            dialogTitle={
                <LocalizedStrict id="create-order-drawer-repeat-order">
                    <>Repeat order</>
                </LocalizedStrict>
            }
            open={repeatOrderDialog}
            onClose={onClose}
            maxWidthProp="sm"
            scroll="body"
        >
            <DialogContent className={classes.dialogContent}>
                <Formik<RepeatOrderFormValues>
                    enableReinitialize
                    initialValues={prepareInitialValues(initialValues)}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    <Form>
                        <RepeatOrderDialogForm deliveries={deliveries} />
                    </Form>
                </Formik>
            </DialogContent>
        </StandardDialog>
    )
}
